<template>
  <div class="app-layout">
    <!-- Sidebar -->
    <v-navigation-drawer
      ref="sidebar"
      v-model="drawer"
      :temporary="isMobile"
      :permanent="!isMobile"
      :mini-variant="!isMobile && miniDrawer"
      app
      width="260"
      class="app-sidebar"
    >
      <!-- User profile -->
      <div class="sidebar-header" v-if="!miniDrawer || isMobile">
        <v-avatar size="40" color="primary" class="sidebar-avatar">
          <v-img v-if="getUserAvatar()" :src="getUserAvatar()" :alt="getAuthenticatedUser()" contain />
          <v-icon v-else color="white" size="20">mdi-account</v-icon>
        </v-avatar>
        <div class="sidebar-user-info">
          <div class="sidebar-user-name">{{ getAuthenticatedUser() }}</div>
          <div class="sidebar-user-role">{{ getAuthenticatedUserRole() }}</div>
        </div>
      </div>

      <v-divider class="mx-3"></v-divider>

      <!-- Navigation -->
      <v-list dense nav class="pa-2">
        <!-- Dashboard -->
        <v-list-item v-if="canAccessDashboard" to="/" exact class="nav-item">
          <v-list-item-icon>
            <v-icon size="20">mdi-view-dashboard-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Tableau de Bord</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Search -->
        <v-list-item to="/search" class="nav-item">
          <v-list-item-icon>
            <v-icon size="20">mdi-magnify</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Rechercher</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Grouped navigation -->
        <v-list-group
          v-for="item in visibleMenuItems"
          :key="item.title"
          v-model="item.active"
          no-action
          class="nav-group"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <img
                v-if="typeof item.action === 'string' && item.action.endsWith('.svg')"
                :src="item.action"
                alt="icon"
                class="nav-svg-icon"
              />
              <v-icon v-else size="20">{{ item.action }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="child in item.items"
            :key="child.title"
            :to="child.link"
            class="nav-child"
          >
            <v-list-item-content>
              <v-list-item-title>{{ child.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>

        <!-- Users -->
        <v-list-item to="/users" class="nav-item">
          <v-list-item-icon>
            <v-icon size="20">mdi-account-group-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Utilisateurs</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Aide de cette page (mobile: header is too narrow for the "?" button) -->
        <v-list-item v-if="isMobile" data-help-bubble @click="openHelp" class="nav-item">
          <v-list-item-icon>
            <v-icon size="20">mdi-help-circle-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Aide de cette page</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Aide -->
        <v-list-item to="/aide" class="nav-item">
          <v-list-item-icon>
            <v-icon size="20">mdi-help-circle-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Aide</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-2">
          <v-list-item @click="logout" class="nav-item nav-logout">
            <v-list-item-icon>
              <v-progress-circular v-if="loading" indeterminate size="20" width="2" color="error" />
              <v-icon v-else size="20" color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="error--text">Déconnexion</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Top bar -->
    <v-app-bar app flat color="white" class="app-topbar" height="48">
      <div class="topbar-content">
        <v-btn icon small @click.stop="toggleDrawer">
          <v-icon size="22">{{ isMobile ? 'mdi-menu' : (miniDrawer ? 'mdi-menu' : 'mdi-menu-open') }}</v-icon>
        </v-btn>

        <div class="topbar-logo" @click="$router.push('/')">
          <img :src="colorLogo" alt="KES LAKAY" class="topbar-logo-img" />
        </div>

        <div v-if="periodRangeLabel" class="topbar-period" :title="periodRangeLabel">
          <v-icon size="16" color="primary" class="topbar-period-icon">mdi-calendar-range</v-icon>
          <span class="topbar-period-text">{{ periodRangeLabel }}</span>
        </div>

        <v-spacer></v-spacer>

        <v-btn icon small @click="filterdialog = true" title="Filtres">
          <v-icon size="22" color="primary">mdi-filter-variant</v-icon>
        </v-btn>

        <button
          v-if="!isMobile"
          type="button"
          class="help-header-btn"
          data-help-bubble
          title="Aide"
          aria-label="Aide"
          @click="openHelp"
        >
          <span class="help-header-btn-q">?</span>
        </button>
      </div>
    </v-app-bar>

    <!-- Main content -->
    <v-main class="app-main">
      <router-view></router-view>
    </v-main>

    <!-- Filter dialog -->
    <v-dialog v-model="filterdialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
          <v-icon color="primary" class="mr-2" size="20">mdi-filter-variant</v-icon>
          Filtres
        </v-card-title>

        <v-card-text class="pt-6">
          <v-text-field
            v-model="debut_periode"
            type="date"
            label="Début de période"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
          ></v-text-field>

          <v-text-field
            v-model="fin_periode"
            type="date"
            label="Fin de période"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
          ></v-text-field>

          <v-select
            v-model="rate_id"
            :items="rates"
            item-text="label"
            item-value="id"
            label="Devise"
            outlined
            dense
            hide-details="auto"
          ></v-select>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="filterdialog = false">Annuler</v-btn>
          <v-btn color="primary" depressed @click="filter">
            <v-icon left small>mdi-check</v-icon>
            Appliquer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Contextual help slide-over (triggered from the header "?" button) -->
    <HelpBubble ref="helpBubble" />
  </div>
</template>

<script>
import moment from 'moment'
import logo from "/src/assets/img/white_emblem.png"
import colorLogo from "/src/assets/img/logo.png"
import { persistUsdContext } from '../../utils/usdPersistence'
import { canCurrentUserAccessLink, isCurrentUserSupervisor } from '../../utils/accessControl'
import HelpBubble from '@/components/HelpBubble.vue'

export default {
  components: { HelpBubble },
  data() {
    return {
      // Initialisés d'après la largeur RÉELLE dès la création : avec les anciens
      // défauts (isMobile:false, drawer:true), le premier rendu sur tablette
      // portrait montait le tiroir en `permanent` ouvert, puis le checkMobile()
      // du mounted() le refermait — mais l'émission input=true du drawer
      // (montage Vuetify) écrasait ce false : le menu s'ouvrait tout seul en
      // overlay à chaque chargement, en recouvrant le bouton burger.
      isMobile: window.innerWidth < 960,
      miniDrawer: false,
      drawer: window.innerWidth >= 960,
      rate_id: 1,
      debut_periode: null,
      fin_periode: null,
      filterdialog: false,
      periodStorageStamp: 0,
      loading: false,
      logo: logo,
      colorLogo: colorLogo,
      business: null,
      storeSettings: { enable_table_management: false, enable_kitchen_printing: false, enable_bar_printing: false, enable_composed_products: false },
      rates: [
        { id: 1, label: 'HTG' },
        { id: 2, label: 'USD' },
        { id: 3, label: 'LES DEUX' }
      ],
      listItems: [
        {
          action: 'mdi-chart-box-outline',
          active: false,
          items: [
            { title: 'Récapitulatif', link: '/sales-summary' },
            { title: 'Ventes', link: '/sales' },
            { title: 'Retours', link: '/returns' },
            { title: 'Produits', link: '/productsreport' },
            { title: 'Agents', link: '/agents' },
            { title: 'Caisse', link: '/closing' },
          ],
          title: 'Rapports',
        },
        {
          title: 'Catalogue',
          action: 'mdi-shopping-outline',
          active: false,
          items: [
            { title: 'Catégories', link: '/categories' },
            { title: 'Produits', link: '/products' },
            { title: 'Mises à Jours', link: '/catalog' },
            { title: 'Import', link: '/import' },
            { title: 'Migration Loyverse', link: '/loyverse/import' },
          ],
        },
        {
          title: 'Inventaire',
          action: 'mdi-clipboard-list-outline',
          active: false,
          items: [
            { title: 'Nouveau Lot', link: '/new-batche' },
            { title: 'Liste des Lots', link: '/batches' },
            { title: 'Rapport de Stock', link: '/stock' },
            { title: 'Mouvements Stock', link: '/stock-movements' },
            { title: 'Alertes Stock', link: '/stock-alerts' },
          ],
        },
        {
          title: 'Clients',
          action: 'mdi-account-circle-outline',
          active: false,
          items: [
            { title: 'Liste', link: '/customers' },
          ],
        },
        {
          title: 'Restaurant',
          action: 'mdi-silverware-fork-knife',
          active: false,
          requiresRestaurant: true,
          items: [
            { title: 'Tables', link: '/restaurant/tables' },
            { title: 'Commandes', link: '/restaurant/orders' },
            { title: 'Serveurs', link: '/restaurant/servers' },
            { title: 'Commissions', link: '/restaurant/commissions' },
            { title: 'Codes Superviseur', link: '/restaurant/codes' },
          ],
        },
        {
          title: 'Ingrédients',
          action: 'mdi-food-variant',
          active: false,
          requiresComposed: true,
          items: [
            { title: 'Liste', link: '/ingredients' },
            { title: 'Compositions', link: '/compositions' },
            { title: 'Mouvements', link: '/ingredient-movements' },
            { title: 'Rapport de Stock', link: '/ingredient-stock' },
            { title: 'Alertes', link: '/ingredient-alerts' },
            { title: 'Consommation', link: '/ingredients-report' },
          ],
        },
        // Bascule KL-Pay (2026-07) : les entrées NATCASH/MONCASH sont masquées —
        // les vues restent en place pour l'historique des retraits legacy si besoin.
        {
          action: 'mdi-contactless-payment',
          active: false,
          items: [
            { title: 'Configuration', link: '/klpay/settings' },
            { title: 'Transactions', link: '/payments/klpay' },
          ],
          title: 'KL PAY',
        },
        {
          action: 'mdi-cog-outline',
          active: false,
          items: [
            { title: 'Profil', link: '/account' },
            { title: 'Emplacement', link: '/stores' },
            { title: 'Appareils', link: '/devices' },
            { title: 'Taux du Jour', link: '/rates' },
            { title: 'Configurations', link: '/configurations' },
            { title: 'Messages Relevés', link: '/statement-settings' },
          ],
          title: 'Compte',
        },
      ],
    }
  },

  computed: {
    visibleMenuItems() {
      return this.listItems
        .filter(item => {
          if (item.requiresRestaurant) {
            return this.storeSettings.enable_table_management || this.storeSettings.enable_kitchen_printing
          }
          if (item.requiresComposed) {
            return !!this.storeSettings.enable_composed_products
          }
          return true
        })
        .map(item => ({
          ...item,
          items: item.items.filter(child => canCurrentUserAccessLink(child.link))
        }))
        .filter(item => item.items.length > 0)
    },
    isSupervisor() {
      return isCurrentUserSupervisor()
    },
    canAccessDashboard() {
      return canCurrentUserAccessLink('/')
    },
    /** Libellé persisté (localStorage), indépendant des champs du dialogue tant qu’on n’a pas appliqué. */
    periodRangeLabel() {
      void this.periodStorageStamp
      const md = this.parsePeriodDate(localStorage.getItem('debut_periode'))
      const mf = this.parsePeriodDate(localStorage.getItem('fin_periode'))
      if (!md || !mf) return ''
      const fd = md.format('DD/MM/YY')
      const ff = mf.format('DD/MM/YY')
      return fd === ff ? fd : fd + ' au ' + ff
    },
  },

  methods: {
    parsePeriodDate(raw) {
      if (raw == null || raw === '' || String(raw) === 'undefined' || String(raw) === 'null') return null
      const m = moment(raw, ['YYYY-MM-DD', moment.ISO_8601], true)
      if (!m.isValid()) return null
      return m
    },
    openHelp() {
      if (this.isMobile) this.drawer = false
      if (this.$refs.helpBubble) this.$refs.helpBubble.toggle()
    },

    toggleDrawer() {
      if (this.isMobile) {
        // Se baser sur l'état RÉEL du tiroir (isActive), pas seulement sur le
        // v-model : si les deux divergent (course permanent/temporary lors d'une
        // rotation ou d'un resize), un simple `!this.drawer` peut ne rien faire
        // à l'écran — le burger semble mort et le menu impossible à rouvrir.
        const sidebar = this.$refs.sidebar
        const actuallyOpen = sidebar ? sidebar.isActive : this.drawer
        this.drawer = !actuallyOpen
        if (sidebar && sidebar.isActive !== this.drawer) {
          sidebar.isActive = this.drawer
        }
      } else {
        this.miniDrawer = !this.miniDrawer
      }
    },

    checkMobile() {
      const nextMobile = window.innerWidth < 960
      const wasMobile = this.isMobile
      this.isMobile = nextMobile

      if (nextMobile) {
        this.miniDrawer = false
        // Ne pas forcer drawer=false à chaque resize sur mobile : l’overlay du menu
        // déclenche souvent un resize et refermait le tiroir immédiatement.
        if (!wasMobile) {
          this.drawer = false
        }
      } else {
        this.drawer = true
      }
    },

    logout: async function () {
      this.loading = true
      localStorage.setItem("debut_periode", null)
      localStorage.setItem("fin_periode", null)
      await this.$store.dispatch('LogOut')
      this.$router.push('/login')
    },

    setFilters() {
      var debut_periode = localStorage.getItem("debut_periode") || moment().subtract(1, 'day').format('YYYY-MM-DD')
      var fin_periode = localStorage.getItem("fin_periode") || moment().format('YYYY-MM-DD')

      this.debut_periode = (debut_periode && debut_periode !== 'undefined' && debut_periode !== '')
        ? debut_periode
        : moment().subtract(1, 'day').format('YYYY-MM-DD')

      this.fin_periode = (fin_periode && fin_periode !== 'undefined' && fin_periode !== '')
        ? fin_periode
        : moment().format('YYYY-MM-DD')

      this.rate_id = 1

      localStorage.setItem("filter_rate_id", this.rate_id)
      localStorage.setItem("debut_periode", this.debut_periode)
      localStorage.setItem("fin_periode", this.fin_periode)
      this.periodStorageStamp++
    },

    filter() {
      localStorage.setItem("filter_rate_id", this.rate_id)
      localStorage.setItem("debut_periode", this.debut_periode)
      localStorage.setItem("fin_periode", this.fin_periode)
      this.periodStorageStamp++

      this.$router.push({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          from: this.debut_periode,
          to: this.fin_periode,
        }
      }).then(() => {
        window.location.reload()
      })
    },

    getAuthenticatedUser() {
      try {
        return JSON.parse(localStorage.authentication).user.full_name
      } catch { return '' }
    },

    getAuthenticatedUserRole() {
      try {
        return JSON.parse(localStorage.authentication).user.role.name
      } catch { return '' }
    },

    getSchema() {
      try {
        return JSON.parse(localStorage.authentication).schema
      } catch { return '' }
    },

    getUserAvatar() {
      try {
        return JSON.parse(localStorage.authentication || '{}').user?.avatar_url || null
      } catch { return null }
    },

    getBusinessLogo() {
      if (!this.business?.logo_path) return null
      if (this.business.logo_path.startsWith('http')) return this.business.logo_path
      return `https://keslakay.s3.us-east-1.amazonaws.com/${this.business.logo_path}`
    },

    async loadStoreSettings() {
      try {
        var business_id = JSON.parse(localStorage.business || '{}').id
        if (!business_id) return
        let result = await this.$axios.get('/stores?orderasc=name&include=store_rates&business_id=' + business_id)
        if (result.data && result.data.length > 0) {
          var store = result.data[0]
          this.storeSettings = {
            enable_table_management: store.enable_table_management || false,
            enable_kitchen_printing: store.enable_kitchen_printing || false,
            enable_bar_printing: store.enable_bar_printing || false,
            enable_composed_products: store.enable_composed_products || false,
          }
          localStorage.setItem('storeSettings', JSON.stringify(this.storeSettings))
          const stores = result.data
          const anyUsd = stores.some(function (s) { return Number(s.rate_id) === 2 })
          var salesRate = null
          if (anyUsd) {
            var rr = await this.$axios.get('/store-rates?include=store')
            var rows = rr.data || []
            for (var k = 0; k < rows.length; k++) {
              if (Number(rows[k].rate_id) === 2) salesRate = rows[k].sales_rate
            }
          }
          persistUsdContext(anyUsd, salesRate)
        }
      } catch (e) {
        // Try from cache
        try {
          var cached = JSON.parse(localStorage.getItem('storeSettings') || '{}')
          if (cached.enable_table_management !== undefined) {
            this.storeSettings = cached
          }
        } catch (_) { /* ignore cache read errors */ }
      }
    },

    async getBusiness() {
      try {
        let result = await this.$axios.get('/businesses?prefix=' + this.getSchema())

        if (result == undefined) {
          await this.$store.dispatch('LogOut')
          this.$router.push('/login')
          return
        }

        // La garde portait sur `result` et non sur `result.data[0]` : quand l'API
        // renvoyait un tableau vide (hoquet réseau, réponse partielle), on faisait
        // `JSON.stringify(undefined)` -> `undefined`, et `setItem` écrivait la CHAÎNE
        // "undefined" dans localStorage.business. Tout `JSON.parse(localStorage.business)`
        // levait alors "SyntaxError: unexpected character at line 1 column 1" jusqu'au
        // prochain rechargement réussi. On n'écrit donc que si le business existe, et on
        // conserve la valeur précédente (valide) sinon.
        const business = result.data && result.data[0]
        if (business) {
          this.business = business
          localStorage.setItem("business", JSON.stringify(business))
        } else {
          console.warn('getBusiness: réponse sans business, localStorage.business conservé')
        }
      } catch (e) {
        console.error('Error loading business:', e)
      }
    },

    onBusinessUpdated(event) {
      this.business = event.detail.business
    },

    onStoreSettingsUpdated(event) {
      this.storeSettings = event.detail
    }
  },

  watch: {
    '$route'() {
      this.getBusiness()
      // Close drawer on mobile after navigation
      if (this.isMobile) {
        this.drawer = false
      }
    }
  },

  mounted() {
    this.getBusiness()
      .then(() => this.loadStoreSettings())
      .catch(() => this.loadStoreSettings())
    this.setFilters()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('businessUpdated', this.onBusinessUpdated)
    window.addEventListener('storeSettingsUpdated', this.onStoreSettingsUpdated)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('businessUpdated', this.onBusinessUpdated)
    window.removeEventListener('storeSettingsUpdated', this.onStoreSettingsUpdated)
  }
}
</script>

<style lang="scss" scoped>
.app-layout {
  min-height: 100vh;
}

/* Sidebar */
.app-sidebar {
  border-right: 1px solid var(--kl-border, #e8ecf1) !important;
  background: #ffffff !important;
  box-shadow: none !important;
}

.sidebar-header {
  display: flex;
  align-items: center;
  padding: 20px 16px;
  gap: 12px;
}

.sidebar-avatar {
  flex-shrink: 0;
}

.sidebar-user-info {
  min-width: 0;
  flex: 1;
}

.sidebar-user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--kl-text, #1a2332);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-user-role {
  font-size: 0.75rem;
  color: var(--kl-text-secondary, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Shared icon alignment for ALL nav items (standalone + group headers) */
::v-deep .v-list-item__icon {
  margin-right: 12px !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  align-self: center !important;
  height: 24px !important;
  min-width: 24px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

/* Navigation items - standalone */
.nav-item {
  min-height: 40px !important;
  margin-bottom: 2px;
  border-radius: 8px;
  padding-left: 8px !important;
  padding-right: 8px !important;

  .v-list-item__title {
    font-size: 0.8125rem !important;
    font-weight: 500;
    line-height: 1.25;
  }
}

.nav-item.v-list-item--active {
  background: rgba(25, 65, 115, 0.08) !important;

  .v-icon {
    color: var(--kl-primary, #194173) !important;
  }

  .v-list-item__title {
    color: var(--kl-primary, #194173) !important;
    font-weight: 600;
  }
}

.nav-svg-icon {
  width: 20px;
  height: 20px;
  display: block;
}

/* Navigation groups */
.nav-group {
  margin-bottom: 2px;

  ::v-deep .v-list-group__header {
    min-height: 40px !important;
    border-radius: 8px;
    margin: 2px 8px;
    padding-left: 8px !important;
    padding-right: 8px !important;

    .v-list-item__title {
      font-size: 0.8125rem !important;
      font-weight: 500;
      line-height: 1.25;
    }
  }

  ::v-deep .v-list-group__header__append-icon {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    align-self: center !important;
    min-width: 24px !important;
  }
}

/* Child items inside groups */
.nav-child {
  min-height: 36px !important;
  padding-left: 52px !important;

  .v-list-item__title {
    font-size: 0.8125rem !important;
  }
}

.nav-child.v-list-item--active {
  background: rgba(25, 65, 115, 0.08) !important;

  .v-list-item__title {
    color: var(--kl-primary, #194173) !important;
    font-weight: 600;
  }
}

.nav-logout:hover {
  background: rgba(244, 67, 54, 0.08) !important;
}

/* Top bar */
.app-topbar {
  border-bottom: 1px solid var(--kl-border, #e8ecf1) !important;
  box-shadow: none !important;
}

.topbar-content {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  gap: 8px;
}

.topbar-logo {
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 100%;
}

/* Contextual help button (matches the original floating bubble's gradient) */
.help-header-btn {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #6d5ef6, #2d8cff);
  box-shadow: 0 6px 16px -6px rgba(45, 140, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.15s ease;
}

.help-header-btn:hover {
  filter: brightness(1.07);
}

.help-header-btn-q {
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.topbar-logo-img {
  height: 28px;
  width: auto;
  object-fit: contain;
}

.topbar-period {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: min(42vw, 280px);
  margin-left: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(25, 65, 115, 0.06);
}

.topbar-period-icon {
  flex-shrink: 0;
}

.topbar-period-text {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--kl-text-secondary, #64748b);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Main content */
.app-main {
  background: var(--kl-bg, #f5f7fa);
}
</style>
